export const STORAGE_KEYS = {
  authToken: 'db_scaffolding.auth.token',
  refreshToken: 'db_scaffolding.auth.refresh',
  authUser: 'db_scaffolding.auth.user',
  theme: 'db_scaffolding.theme',
  cart: 'db_scaffolding.cart',
  heldSales: 'db_scaffolding.held_sales',
  settings: 'db_scaffolding.settings',
  roles: 'db_scaffolding.roles',
  sidebar: 'db_scaffolding.sidebar',
} as const;

export const ROLES = {
  ADMIN: 'admin',
  CASHIER: 'cashier',
  MANAGER: 'manager',
  SALES: 'sales',
} as const;

export type Role = (typeof ROLES)[keyof typeof ROLES];

export const PERMISSIONS = {
  DASHBOARD_VIEW: 'dashboard:view',
  POS: 'sales:pos',
  SALES_VIEW: 'sales:view',
  SALES_REFUND: 'sales:refund',
  SALES_CREDIT: 'sales:credit',
  SALES_QUOTES: 'sales:quotes',
  SALES_RECEIVABLES: 'sales:receivables',
  PRODUCTS_VIEW: 'products:view',
  PRODUCTS_MANAGE: 'products:manage',
  PRODUCTS_DELETE: 'products:delete',
  CATEGORIES_VIEW: 'products:categories',
  PURCHASES_VIEW: 'purchases:view',
  PURCHASES_CREATE: 'purchases:create',
  STOCK_VIEW: 'stock:view',
  STOCK_IN: 'stock:in',
  STOCK_OUT: 'stock:out',
  SUPPLIERS_VIEW: 'suppliers:view',
  SUPPLIERS_CREATE: 'suppliers:create',
  CUSTOMERS_VIEW: 'customers:view',
  CUSTOMERS_CREATE: 'customers:create',
  CUSTOMERS_DELETE: 'customers:delete',
  EXPENSES_VIEW: 'expenses:view',
  EXPENSES_CREATE: 'expenses:create',
  EMPLOYEES_VIEW: 'employees:view',
  EMPLOYEES_MANAGE: 'employees:manage',
  REPORTS_VIEW: 'reports:view',
  USERS_VIEW: 'users:view',
  USERS_CREATE: 'users:create',
  USERS_DELETE: 'users:delete',
  ROLES_MANAGE: 'roles:manage',
  SETTINGS_MANAGE: 'settings:manage',
  AUDIT_VIEW: 'audit:view',
  INVENTORY_MANAGE: 'inventory:manage',
  USERS_MANAGE: 'users:manage',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export const ROLE_PERMISSIONS: Record<Role, Permission[]> = {
  admin: Object.values(PERMISSIONS),
  manager: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.POS,
    PERMISSIONS.SALES_VIEW,
    PERMISSIONS.SALES_REFUND,
    PERMISSIONS.SALES_QUOTES,
    PERMISSIONS.SALES_RECEIVABLES,
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.PRODUCTS_MANAGE,
    PERMISSIONS.CATEGORIES_VIEW,
    PERMISSIONS.PURCHASES_VIEW,
    PERMISSIONS.PURCHASES_CREATE,
    PERMISSIONS.STOCK_VIEW,
    PERMISSIONS.STOCK_IN,
    PERMISSIONS.STOCK_OUT,
    PERMISSIONS.SUPPLIERS_VIEW,
    PERMISSIONS.SUPPLIERS_CREATE,
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
    PERMISSIONS.EXPENSES_VIEW,
    PERMISSIONS.EXPENSES_CREATE,
    PERMISSIONS.EMPLOYEES_VIEW,
    PERMISSIONS.EMPLOYEES_MANAGE,
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.SETTINGS_MANAGE,
    PERMISSIONS.INVENTORY_MANAGE,
  ],
  cashier: [
    PERMISSIONS.DASHBOARD_VIEW,
    PERMISSIONS.POS,
    PERMISSIONS.SALES_VIEW,
    PERMISSIONS.SALES_QUOTES,
    PERMISSIONS.SALES_RECEIVABLES,
    PERMISSIONS.PRODUCTS_VIEW,
    PERMISSIONS.CUSTOMERS_VIEW,
    PERMISSIONS.CUSTOMERS_CREATE,
  ],
  sales: [PERMISSIONS.POS],
};

export interface PermissionMeta {
  id: Permission;
  label: string;
  group: string;
}

export const PERMISSION_CATALOG: PermissionMeta[] = [
  { id: PERMISSIONS.DASHBOARD_VIEW, label: 'Dashboard', group: 'Overview' },

  { id: PERMISSIONS.POS, label: 'Point of Sale', group: 'Sales' },
  { id: PERMISSIONS.SALES_VIEW, label: 'All Sales', group: 'Sales' },
  { id: PERMISSIONS.SALES_REFUND, label: 'Refunds', group: 'Sales' },
  { id: PERMISSIONS.SALES_CREDIT, label: 'Credit sales', group: 'Sales' },
  { id: PERMISSIONS.SALES_QUOTES, label: 'Quotations', group: 'Sales' },
  { id: PERMISSIONS.SALES_RECEIVABLES, label: 'Receivables', group: 'Sales' },

  { id: PERMISSIONS.PRODUCTS_VIEW, label: 'All Products', group: 'Products' },
  { id: PERMISSIONS.PRODUCTS_MANAGE, label: 'Create / edit products', group: 'Products' },
  { id: PERMISSIONS.PRODUCTS_DELETE, label: 'Delete products', group: 'Products' },
  { id: PERMISSIONS.CATEGORIES_VIEW, label: 'Categories', group: 'Products' },

  { id: PERMISSIONS.PURCHASES_VIEW, label: 'All Orders', group: 'Purchases' },
  { id: PERMISSIONS.PURCHASES_CREATE, label: 'Create purchase order', group: 'Purchases' },
  { id: PERMISSIONS.STOCK_VIEW, label: 'Stock Levels', group: 'Purchases' },
  { id: PERMISSIONS.STOCK_IN, label: 'Stock in', group: 'Purchases' },
  { id: PERMISSIONS.STOCK_OUT, label: 'Stock out', group: 'Purchases' },

  { id: PERMISSIONS.SUPPLIERS_VIEW, label: 'All Suppliers', group: 'Suppliers' },
  { id: PERMISSIONS.SUPPLIERS_CREATE, label: 'Create supplier', group: 'Suppliers' },

  { id: PERMISSIONS.CUSTOMERS_VIEW, label: 'All Customers', group: 'Customers' },
  { id: PERMISSIONS.CUSTOMERS_CREATE, label: 'Create customer', group: 'Customers' },
  { id: PERMISSIONS.CUSTOMERS_DELETE, label: 'Delete customer', group: 'Customers' },

  { id: PERMISSIONS.EXPENSES_VIEW, label: 'All Expenses', group: 'Expenses' },
  { id: PERMISSIONS.EXPENSES_CREATE, label: 'Create expense', group: 'Expenses' },

  { id: PERMISSIONS.EMPLOYEES_VIEW, label: 'All Employees', group: 'Employees' },
  { id: PERMISSIONS.EMPLOYEES_MANAGE, label: 'Create / edit employees', group: 'Employees' },

  { id: PERMISSIONS.USERS_VIEW, label: 'All Users', group: 'Users & Access' },
  { id: PERMISSIONS.USERS_CREATE, label: 'Create user', group: 'Users & Access' },
  { id: PERMISSIONS.USERS_DELETE, label: 'Delete user', group: 'Users & Access' },
  { id: PERMISSIONS.ROLES_MANAGE, label: 'Roles', group: 'Users & Access' },

  { id: PERMISSIONS.REPORTS_VIEW, label: 'Reports', group: 'Reports' },

  { id: PERMISSIONS.SETTINGS_MANAGE, label: 'Settings', group: 'System' },
  { id: PERMISSIONS.AUDIT_VIEW, label: 'Audit Logs', group: 'System' },
];

export const PAYMENT_METHODS = [
  { id: 'cash', label: 'Cash' },
  { id: 'mpesa', label: 'M-PESA' },
] as const;

export function enabledPaymentMethods(enabledMethodIds: readonly string[]) {
  return enabledMethodIds.length
    ? PAYMENT_METHODS.filter((m) => enabledMethodIds.includes(m.id))
    : PAYMENT_METHODS;
}

export function creditPaymentMethods(enabledMethodIds: readonly string[]) {
  const isEnabled = (id: string) => enabledMethodIds.length === 0 || enabledMethodIds.includes(id);
  return PAYMENT_METHODS.filter((m) => (m.id === 'cash' || m.id === 'mpesa') && isEnabled(m.id));
}

export const DEFAULT_CURRENCY = {
  code: 'KES',
  symbol: 'Ksh ',
  position: 'before' as const,
};

export interface CurrencyOption {
  code: string;
  name: string;
  symbol: string;
}

export const CURRENCIES: CurrencyOption[] = [
  { code: 'AOA', name: 'Angolan Kwanza', symbol: 'Kz' },
  { code: 'ARS', name: 'Argentine Peso', symbol: '$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'AZN', name: 'Azerbaijani Manat', symbol: '₼' },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳' },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв' },
  { code: 'BIF', name: 'Burundian Franc', symbol: 'FBu' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  { code: 'BWP', name: 'Botswana Pula', symbol: 'P' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CLP', name: 'Chilean Peso', symbol: '$' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'COP', name: 'Colombian Peso', symbol: '$' },
  { code: 'CRC', name: 'Costa Rican Colón', symbol: '₡' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr' },
  { code: 'DOP', name: 'Dominican Peso', symbol: 'RD$' },
  { code: 'DZD', name: 'Algerian Dinar', symbol: 'دج' },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£' },
  { code: 'ETB', name: 'Ethiopian Birr', symbol: 'Br' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'FJD', name: 'Fijian Dollar', symbol: 'FJ$' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'GEL', name: 'Georgian Lari', symbol: '₾' },
  { code: 'GHS', name: 'Ghanaian Cedi', symbol: '₵' },
  { code: 'GTQ', name: 'Guatemalan Quetzal', symbol: 'Q' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft' },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'ISK', name: 'Icelandic Króna', symbol: 'kr' },
  { code: 'JMD', name: 'Jamaican Dollar', symbol: 'J$' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'KES', name: 'Kenyan Shilling', symbol: 'KSh' },
  { code: 'KHR', name: 'Cambodian Riel', symbol: '៛' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩' },
  { code: 'KZT', name: 'Kazakhstani Tenge', symbol: '₸' },
  { code: 'LKR', name: 'Sri Lankan Rupee', symbol: 'Rs' },
  { code: 'MAD', name: 'Moroccan Dirham', symbol: 'DH' },
  { code: 'MGA', name: 'Malagasy Ariary', symbol: 'Ar' },
  { code: 'MMK', name: 'Myanmar Kyat', symbol: 'K' },
  { code: 'MUR', name: 'Mauritian Rupee', symbol: '₨' },
  { code: 'MWK', name: 'Malawian Kwacha', symbol: 'MK' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM' },
  { code: 'MZN', name: 'Mozambican Metical', symbol: 'MT' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
  { code: 'NPR', name: 'Nepalese Rupee', symbol: 'रू' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$' },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱' },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨' },
  { code: 'PLN', name: 'Polish Złoty', symbol: 'zł' },
  { code: 'PYG', name: 'Paraguayan Guarani', symbol: '₲' },
  { code: 'QAR', name: 'Qatari Riyal', symbol: '﷼' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽' },
  { code: 'RWF', name: 'Rwandan Franc', symbol: 'FRw' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
  { code: 'SSP', name: 'South Sudanese Pound', symbol: '£' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿' },
  { code: 'TND', name: 'Tunisian Dinar', symbol: 'د.ت' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺' },
  { code: 'TTD', name: 'Trinidad & Tobago Dollar', symbol: 'TT$' },
  { code: 'TZS', name: 'Tanzanian Shilling', symbol: 'TSh' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', symbol: '₴' },
  { code: 'UGX', name: 'Ugandan Shilling', symbol: 'USh' },
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'UYU', name: 'Uruguayan Peso', symbol: '$U' },
  { code: 'UZS', name: 'Uzbek Som', symbol: "so'm" },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫' },
  { code: 'XAF', name: 'Central African CFA Franc', symbol: 'FCFA' },
  { code: 'XCD', name: 'East Caribbean Dollar', symbol: 'EC$' },
  { code: 'XOF', name: 'West African CFA Franc', symbol: 'CFA' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R' },
  { code: 'ZMW', name: 'Zambian Kwacha', symbol: 'ZK' },
  { code: 'ZWL', name: 'Zimbabwean Dollar', symbol: 'Z$' },
];

export type CurrencyMeta = { code: string; symbol: string; position: 'before' | 'after' };

/** Display meta for an ISO currency code; unknown/empty codes fall back to the store default. */
export function currencyMeta(code?: string | null): CurrencyMeta {
  if (!code || code === DEFAULT_CURRENCY.code) return DEFAULT_CURRENCY;
  const found = CURRENCIES.find((c) => c.code === code);
  return found ? { code: found.code, symbol: found.symbol, position: 'before' } : DEFAULT_CURRENCY;
}

export const CURRENCY_OPTIONS = CURRENCIES.map((c) => ({
  value: c.code,
  label: `${c.code} · ${c.name}`,
}));

export const PAGE_SIZE = 20;
